import type { AutomationAction } from "../lifecycle-actions";
import type { CompletionEffects } from "../task";
import { systemTaskType } from "./system-task-types";
import type { Resolution, TaskFlow, TaskType } from "./task-type";

/**
 * The next tasks a resolution allows: its default when it finalizes the case, otherwise
 * the flow's next tasks, plus `null` (none) only when the resolution has no default.
 */
export const nextTaskOptions = (
  flow: Pick<TaskFlow, "nextTasks">,
  resolution: Pick<Resolution, "defaultNextTask" | "finalizesCase">,
): (string | null)[] => {
  if (resolution.finalizesCase) return [resolution.defaultNextTask];
  return [...(resolution.defaultNextTask === null ? [null] : []), ...flow.nextTasks];
};

/**
 * Validates a completion against its task type and returns the effects to snapshot on the
 * task: null for client and intake-routed tasks, whose follow-ups are not configured.
 */
export const resolveCompletionEffects = (
  taskType: Pick<TaskType, "system" | "scope" | "nextTasks" | "resolutions">,
  resolutionId: string,
  requestedNextTask: string | null | undefined,
):
  | { ok: true; resolution: Resolution; effects: CompletionEffects | null }
  | { ok: false; reason: "invalid_solution" | "invalid_next_task" } => {
  const resolution = taskType.resolutions.find(({ id, active }) => id === resolutionId && active);
  if (!resolution) return { ok: false, reason: "invalid_solution" };
  if (taskType.scope === "client" || systemTaskType(taskType.system)?.intake)
    return requestedNextTask
      ? { ok: false, reason: "invalid_next_task" }
      : { ok: true, resolution, effects: null };
  const nextTaskType =
    requestedNextTask === undefined ? resolution.defaultNextTask : requestedNextTask;
  if (!nextTaskOptions(taskType, resolution).includes(nextTaskType))
    return { ok: false, reason: "invalid_next_task" };
  const { finalizesCase, alsoCreate } = resolution;
  return { ok: true, resolution, effects: { nextTaskType, finalizesCase, alsoCreate } };
};

/** The automation actions a completion's effects call for: its tasks, then any finalization. */
export const completionActions = (
  effects: CompletionEffects,
  event: { taskId: string },
): AutomationAction[] => [
  ...[...new Set([effects.nextTaskType, ...effects.alsoCreate])]
    .filter((id): id is string => id !== null)
    .map((id): AutomationAction => ({
      type: "createTask",
      params: { taskType: id, sourceTaskId: event.taskId },
    })),
  ...(effects.finalizesCase
    ? [{ type: "moveCase", params: { step: "finalized" } } satisfies AutomationAction]
    : []),
];
